import { FC, useEffect, useMemo, useState } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  FormController,
  MuiBox,
  MuiTextField,
} from "../../../../../components";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { BulkCreateProgramUploadWrapper } from "./BulkCreateProgramUpload.style";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useBulkUploadSubmit } from "../../../../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import { useUploadFileMutation } from "../../../../../api/services/client/clientApis";
import FileUploader from "../../../../../components/fileUpload/FileUpload";
import {
  FILE_SIZE_LIMITS,
  FILE_TYPE_KEYS,
  MAX_LENGTH_75,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import { getFileTypeMap, isEmpty } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import {
  BulkProcessFormKey,
  BulkUploadStatusResponse,
} from "../../../modelsAndSchema/Models";
import { BulkTransferSearchParticipantSchema } from "../../../modelsAndSchema/ValidationSchemas";
import { bulkCreateProgramSiteCoreKeys } from "../../../siteCore/siteCoreKeys";

export interface BulkCreateProgramUploadProps {
  pageId: string;
  onBulkUploadSuccess: (data: BulkUploadStatusResponse) => void;
  handleNavigateBack: () => void;
  onUploadReadyStateChange: (isReady: boolean) => void;
  isEditBulkProgram: boolean;
}
export const BulkCreateProgramUpload: FC<BulkCreateProgramUploadProps> = ({
  pageId,
  onBulkUploadSuccess,
  handleNavigateBack,
  onUploadReadyStateChange,
  isEditBulkProgram,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [clearFile, setClearFile] = useState<boolean>(false);
  const {
    commonLabels,
    useFetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
    fetchMediaListImage,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const uploadImage = fetchMediaListImage(
    commonSiteCoreKeys.commonComponentKey,
    commonSiteCoreKeys.browseImage,
    commonSiteCoreKeys.smallImage,
  );

  const {
    mutateAsync: fileUpload,
    isPending: isfileUploadPending,
    isError: isfileUploadError,
  } = useUploadFileMutation();

  const {
    mutateAsync: bulkUploadMutate,
    isPending: isBulkUploadPending,
    isSuccess: isBulkUploadSuccess,
    isError: isBulkUploadError,
    data: bulkUploadData,
  } = useBulkUploadSubmit();

  const getLabel = useFetchLabel(
    bulkCreateProgramSiteCoreKeys.bulkProcessDetails,
  );
  const getUploadLabel = useFetchLabel(
    bulkCreateProgramSiteCoreKeys.uploadTemplate,
  );

  useEffect(() => {
    if (isBulkUploadError || isfileUploadError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isBulkUploadError, isfileUploadError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isBulkUploadPending || isfileUploadPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isBulkUploadPending, isfileUploadPending]);

  useEffect(() => {
    if (isBulkUploadSuccess) {
      onBulkUploadSuccess(bulkUploadData?.data);
      reset();
      setClearFile(true);
      setTimeout(() => setClearFile(false), 200);
    }
  }, [isBulkUploadSuccess]);

  const defaultValues = {
    [BulkProcessFormKey.bulkProcessName]: "",
  };

  const methods = useForm<any>({
    mode: "all",
    defaultValues,
    resolver: yupResolver(
      BulkTransferSearchParticipantSchema({
        ...commonValidationMessages,
        uploadFileRequiredMessage: getUploadLabel(
          bulkCreateProgramSiteCoreKeys.uploadSmartTemplateToProceedMessage,
        ),
      }),
    ),
  });

  const { control, getValues, handleSubmit, reset } = methods;

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };
  const uploadDocument = async () => {
    return await fileUpload({
      documentType: "bulk_program_upload",
      operationType: "add",
      entityType: "programupload",
      file: file,
    });
  };

  const { bulkProcessName, file } = getValues();

  const isUploadReady = useMemo(
    () => !isEmpty(bulkProcessName) && !isEmpty(file),
    [bulkProcessName, file],
  );

  useEffect(() => {
    onUploadReadyStateChange?.(isUploadReady);
  }, [isUploadReady, onUploadReadyStateChange]);

  const onHandleClick = async () => {
    const { bulkProcessName } = getValues();
    const response = await uploadDocument();
    if (response.success) {
      await bulkUploadMutate({
        params: {
          fileName: response.fileName,
          uploadDocumentId: response.documentId,
          name: bulkProcessName,
          action: isEditBulkProgram ? "update" : "",
        },
      });
    }
  };
  return (
    <FormProvider {...methods}>
      <BulkCreateProgramUploadWrapper>
        <MuiBox
          className="accordion-header"
          data-testid="bulk-create-program-upload"
        >
          <AccordionHeader
            title={renderHeader(
              bulkCreateProgramSiteCoreKeys.bulkProcessDetails,
            )}
            isExpanded
          >
            <MuiBox className="name-field">
              <FormController
                key={BulkProcessFormKey.bulkProcessName}
                control={control}
                name={BulkProcessFormKey.bulkProcessName}
              >
                <MuiTextField
                  id={BulkProcessFormKey.bulkProcessName}
                  placeholder={getLabel(
                    bulkCreateProgramSiteCoreKeys.bulkProcessNamePlaceholderText,
                  )}
                  maxLength={MAX_LENGTH_75}
                  shouldTrimSpaces={false}
                  isMandatory
                  label={getLabel(
                    bulkCreateProgramSiteCoreKeys.bulkProcessName,
                  )}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiBox>
          </AccordionHeader>
        </MuiBox>
        <MuiBox className="accordion-header">
          <AccordionHeader
            title={renderHeader(bulkCreateProgramSiteCoreKeys.uploadTemplate)}
            isExpanded
          >
            <MuiBox className="upload-container">
              <FormController control={control} name={BulkProcessFormKey.file}>
                <FileUploader
                  formNameKey={BulkProcessFormKey.file}
                  isFromBulkCreate
                  data-testid={`${pageId}-${BulkProcessFormKey.file}}`}
                  maxFileSize={FILE_SIZE_LIMITS.FIFTEEN_MB}
                  acceptedFileTypes={getFileTypeMap(
                    FILE_TYPE_KEYS.EXCEL,
                    FILE_TYPE_KEYS.EXCEL_X,
                  )}
                  maxFileSizeErrorMessageText={getUploadLabel(
                    bulkCreateProgramSiteCoreKeys.fileExceedsTheSizeLimitMessage,
                  )}
                  fileFormatErrorMessageText={getUploadLabel(
                    bulkCreateProgramSiteCoreKeys.invalidFileFormatMessage,
                  )}
                  sizeLabel={getUploadLabel(
                    bulkCreateProgramSiteCoreKeys.maximumFileSizeandType,
                  )}
                  replaceModalLabel={getUploadLabel(
                    bulkCreateProgramSiteCoreKeys.replaceExistingSmartTemplateConfirmationMessage,
                  )}
                  browseLabel={commonLabels.browse}
                  dropYourFileLabel={commonLabels.dropYourFile}
                  replaceModalTitle={commonLabels.confirmation}
                  replaceModalPrimaryButtonLabel={commonLabels.yes}
                  replaceModalSecondaryButtonLabel={commonLabels.no}
                  browseImage={{
                    src: uploadImage?.ImageUrl,
                    alt: uploadImage?.AltText,
                  }}
                  clearUploadedFile={clearFile}
                />
              </FormController>
            </MuiBox>
          </AccordionHeader>
        </MuiBox>

        <BulkInsertFooter
          pageId={pageId}
          onConfirmClick={handleSubmit(onHandleClick)}
          isFiledUpdated={!isEmpty(bulkProcessName) || !isEmpty(file)}
          confirmButtonText={commonLabels.submit}
          handleNavigateBack={handleNavigateBack}
          cancelMessage={getUploadLabel(
            bulkCreateProgramSiteCoreKeys.cancelBulkCreateProgramConfirmationMessage,
          )}
          cancelTitle={commonLabels.confirmation}
        />
      </BulkCreateProgramUploadWrapper>
    </FormProvider>
  );
};
